import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
    setLoading,
    setSubmitting,
    setBranchDetail,
    updateBranchField,
    resetBranchForm,
    setEstablishmentTypes,
    setBranchList,
    setPaginator,
    setShowRegistrationModal,
    setShowViewModal,
    setShowEditModal,
    updateBranchStatusInList,
} from '../../store/slices/branches/branchConfigurationSlice';

import BranchConfigurationService from '../../services/branch/BranchConfigurationService';
import {
    BranchRegistrationDTO,
    BranchUpdateDTO,
} from '../../types/branches/configuration/BranchTypes';

import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useApiErrorHandler from '../useApiErrorHandler';

export const useBranchConfiguration = () => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        form,
        detail,
        list,
        paginator,
        isLoading,
        isSubmitting,
        error,
        establishmentTypes,
        showRegistrationModal,
        showViewModal,
        showEditModal,
    } = useSelector((state: RootState) => state.branchConfiguration);

    const { show: showAlert } = useAlerts();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handle } = useApiErrorHandler();

    /**
     * ✅ Cargar listado de sucursales paginadas
     */
    const fetchBranchesPaged = async (
        page = 0,
        size = 10,
        sort = 'establishmentCode,asc'
    ) => {
        try {
            dispatch(setLoading(true));
            showLoadingIndicator();

            const response = await BranchConfigurationService.getBranchesPaged(page, size, sort);
            if (response.statusCode === 200) {
                dispatch(setBranchList(response.data.content || []));
                dispatch(setPaginator(response.data)); // si tu backend retorna un Page<T> completo
            } else {
                showAlert('error', 'Error', 'No se pudo obtener la lista de sucursales.');
            }
        } catch (err: any) {
            handle(err);
        } finally {
            dispatch(setLoading(false));
            hideLoadingIndicator();
        }
    };

    /**
 * ✅ Crear o actualizar una sucursal
 */
    const saveBranch = async (
        data: BranchRegistrationDTO | BranchUpdateDTO,
        id?: number
    ) => {
        try {
            dispatch(setSubmitting(true));
            showLoadingIndicator();

            const isUpdate = Boolean(id);
            const response = isUpdate
                ? await BranchConfigurationService.updateBranch(id!, data as BranchUpdateDTO)
                : await BranchConfigurationService.createBranch(data as BranchRegistrationDTO);

            const { statusCode, type, title, message } = response;

            if (statusCode === 200 || statusCode === 201) {
                showAlert(type, title, message);
                resetForm();

                // ✅ Cierra el modal correspondiente según sea creación o edición
                if (isUpdate) {
                    closeEditModal();
                } else {
                    closeAddBranchModal();
                }

                // ✅ Refresca listado
                fetchBranchesPaged();
            } else {
                showAlert('error', 'Error', 'No se pudo guardar la sucursal.');
            }
        } catch (err: any) {
            handle(err);
        } finally {
            dispatch(setSubmitting(false));
            hideLoadingIndicator();
        }
    };


    /**
   * ✅ Obtener sucursal por ID y abrir modal según modo
   * @param id ID de la sucursal
   * @param mode 'view' | 'edit'
   */
    const loadBranchById = async (id: number, mode: 'view' | 'edit') => {
        try {
            dispatch(setLoading(true));
            showLoadingIndicator();

            const response = await BranchConfigurationService.getBranchById(id);
            console.log(response);
            console.log(mode);
            if (response.statusCode === 200) {
                dispatch(setBranchDetail(response.data));

                // 👇 Abrir modal según el modo
                if (mode === 'view') {
                    dispatch(setShowViewModal(true));
                } else if (mode === 'edit') {
                    dispatch(setShowEditModal(true));
                }
            } else {
                showAlert('error', 'Error', 'No se pudo obtener la sucursal.');
            }
        } catch (err: any) {
            handle(err);
        } finally {
            dispatch(setLoading(false));
            hideLoadingIndicator();
        }
    };


    /**
 * ✅ Alternar estado (activar/desactivar)
 */
    const toggleStatus = async (id: number) => {
        try {
            dispatch(setSubmitting(true));
            showLoadingIndicator();

            const response = await BranchConfigurationService.toggleBranchStatus(id);

            if (response.statusCode === 200) {
                // 🔹 Actualizamos el estado en la lista usando el reducer
                dispatch(updateBranchStatusInList({ id, active: response.data.active }));

                // 🔹 Mostramos un mensaje al usuario
                showAlert(response.type, response.title, response.message);
            } else {
                showAlert('error', 'Error', 'No se pudo cambiar el estado de la sucursal.');
            }
        } catch (err: any) {
            handle(err);
        } finally {
            dispatch(setSubmitting(false));
            hideLoadingIndicator();
        }
    };


    /**
     * ✅ Cargar tipos de establecimiento para dropdown
     */
    const loadEstablishmentTypes = async () => {
        try {
            dispatch(setLoading(true));
            showLoadingIndicator();

            const response = await BranchConfigurationService.getEstablishmentTypes();
            if (response.statusCode === 200) {
                dispatch(setEstablishmentTypes(response.data));
            }
        } catch (err: any) {
            handle(err);
        } finally {
            dispatch(setLoading(false));
            hideLoadingIndicator();
        }
    };

    /**
     * ✅ Actualizar un campo del formulario
     */
    const updateField = <K extends keyof BranchRegistrationDTO | keyof BranchUpdateDTO>(
        field: K,
        value: any
    ) => {
        dispatch(updateBranchField({ field, value }));
    };

    /**
     * ✅ Resetear formulario
     */
    const resetForm = () => {
        dispatch(resetBranchForm());
    };

    /**
     * ✅ Control de modales
     */
    const openAddBranchModal = () => {
        dispatch(setShowRegistrationModal(true));
    };

    const closeAddBranchModal = () => {
        dispatch(setShowRegistrationModal(false));
    };

    const openViewModal = () => {
        dispatch(setShowViewModal(true));
    };

    const closeViewModal = () => {
        dispatch(setShowViewModal(false));
    };

    const openEditModal = () => {
        dispatch(setShowEditModal(true));
    };

    const closeEditModal = () => {
        dispatch(setShowEditModal(false));
    };


    return {
        form,
        detail,
        list,
        paginator,
        isLoading,
        isSubmitting,
        error,
        establishmentTypes,
        showRegistrationModal,
        showViewModal,

        fetchBranchesPaged,
        saveBranch,
        loadBranchById,
        toggleStatus,
        loadEstablishmentTypes,
        updateField,
        resetForm,

        openAddBranchModal,
        closeAddBranchModal,
        openViewModal,
        closeViewModal,
        showEditModal,
        openEditModal,
        closeEditModal,
    };
};
